import * as THREE from 'three';
import React, { useState, useEffect, useMemo, useCallback, useRef, forwardRef, useImperativeHandle} from 'react';
import { useFrame } from '@react-three/fiber';

import SatellitePreloader from '../preloaders/SatellitePreloader';
import { getConjunctionsForSatellite } from '../../utils/InitCdm';
import { TrackballControls } from '@react-three/drei';
import { ClusterSatellitesGrid } from './ClusterSatelliteGrid';
import { PLANET_DATA } from '../../utils/PlanetConstants';
import { clearAsset } from '../../utils/AddressableManager';
import { Html } from "@react-three/drei";
import './SatelliteViewer.css'
import * as satellite from 'satellite.js';

// Patch InstancedMesh raycast only once (your existing patch)
// if (!THREE.InstancedMesh.prototype._patched) {
//   THREE.InstancedMesh.prototype.raycast = function (raycaster, intersects) {
//     const geometry = this.geometry;

//     if (!geometry.boundingBox) geometry.computeBoundingBox();
//     if (!geometry.boundingSphere) geometry.computeBoundingSphere();

//     const instanceMatrix = new THREE.Matrix4();
//     const worldMatrix = this.matrixWorld;
//     const finalMatrix = new THREE.Matrix4();
//     const sphere = geometry.boundingSphere.clone();

//     for (let i = 0; i < this.count; i++) {
//       this.getMatrixAt(i, instanceMatrix);

//       finalMatrix.identity(); // Reset before multiplying
//       finalMatrix.multiplyMatrices(worldMatrix, instanceMatrix);

//       sphere.center.set(0, 0, 0).applyMatrix4(finalMatrix);

//       if (raycaster.ray.intersectsSphere(sphere)) {
//         const instanceBox = geometry.boundingBox.clone();
//         instanceBox.applyMatrix4(finalMatrix);

//         if (raycaster.ray.intersectsBox(instanceBox)) {
//           const distance = raycaster.ray.origin.distanceTo(sphere.center);
//           intersects.push({
//             distance,
//             point: sphere.center.clone(),
//             object: this,
//             instanceId: i,
//           });
//         }
//       }
//     }
//   };
//   THREE.InstancedMesh.prototype._patched = true;
// }


if (!THREE.InstancedMesh.prototype._patched) {
  const isTouchDevice =
    'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Extra world-space padding added to each instance's hit-test box,
  // independent of its visual (rendered) scale — keeps dots visually
  // small while making them much easier to actually tap/click.
  const HIT_PADDING = isTouchDevice ? 0.05 : 0.015;

  THREE.InstancedMesh.prototype.raycast = function (raycaster, intersects) {
    const geometry = this.geometry;

    if (!geometry.boundingBox) geometry.computeBoundingBox();
    if (!geometry.boundingSphere) geometry.computeBoundingSphere();

    const instanceMatrix = new THREE.Matrix4();
    const worldMatrix = this.matrixWorld;
    const finalMatrix = new THREE.Matrix4();
    const sphere = geometry.boundingSphere.clone();

    for (let i = 0; i < this.count; i++) {
      this.getMatrixAt(i, instanceMatrix);

      finalMatrix.identity();
      finalMatrix.multiplyMatrices(worldMatrix, instanceMatrix);

      sphere.center.set(0, 0, 0).applyMatrix4(finalMatrix);
      // Pad the broad-phase sphere check too, so it doesn't reject
      // candidates the (now-padded) box check would otherwise accept.
      sphere.radius = geometry.boundingSphere.radius + HIT_PADDING;

      if (raycaster.ray.intersectsSphere(sphere)) {
        const instanceBox = geometry.boundingBox.clone();
        instanceBox.applyMatrix4(finalMatrix);
        instanceBox.expandByScalar(HIT_PADDING); // NEW — the actual fix

        if (raycaster.ray.intersectsBox(instanceBox)) {
          const distance = raycaster.ray.origin.distanceTo(sphere.center);
          intersects.push({
            distance,
            point: sphere.center.clone(),
            object: this,
            instanceId: i,
          });
        }
      }
    }
  };
  THREE.InstancedMesh.prototype._patched = true;
}

export const CATEGORY_COLOR_MAP = {
  // MUST KEEP COLOURS TO 6-BIT ONLY.
  communication: '#e787ff',   
  debris: '#dfdfdf',          
  military: '#C8102E',        
  'earth observation': '#85f868', 
  navigation: '#FFB81C',      
  'space station': '#656ff7', 
  'tech demo': '#8A2BE2',     
  weather: '#00CED1',        
  science: '#016657',         
  unknown: '#6E6E6E',        
  conjunction: '#FF1493', 
};
const SCALE = 1 / 6371;
 
function computeOrbitPath(tleLine1, tleLine2, { steps = 180 } = {}) {
  if (!tleLine1 || !tleLine2) return [];

  let satrec;
  try {
    satrec = satellite.twoline2satrec(tleLine1, tleLine2);
  } catch {
    return [];
  }
  if (!satrec) return [];

  // Derive the satellite's real orbital period from its mean motion,
  // instead of a fixed time window — otherwise satellites with longer
  // periods (MEO/GEO/HEO) only get a partial arc drawn, since a fixed
  // window like 100 minutes only covers a full loop for LEO objects.
  // satrec.no is mean motion in radians/minute; a full revolution is 2π.
  const periodMinutes = satrec.no > 0 ? (2 * Math.PI) / satrec.no : 100;

  // Small safety clamp in case of a degenerate/bad mean motion value —
  // keeps this from ever requesting an absurdly long or zero-length window.
  const minutesAhead = Math.min(Math.max(periodMinutes, 10), 4500);

  const points = [];
  const now = new Date();

  for (let i = 0; i <= steps; i++) {
    const t = new Date(now.getTime() + (i / steps) * minutesAhead * 60 * 1000);

    let posVel;
    try {
      posVel = satellite.propagate(satrec, t);
    } catch {
      continue;
    }

    if (!posVel || !posVel.position || typeof posVel.position !== 'object') continue;
    const { x, y, z } = posVel.position;
    if (![x, y, z].every((v) => typeof v === 'number' && isFinite(v))) continue;

    points.push(new THREE.Vector3(x * SCALE, y * SCALE, z * SCALE));
  }

  return points;
}
// =========================================================
// LIVE PROPAGATION MANAGER
// Propagates satellites in small rotating batches, throttled,
// and exposes interpolated (lerped) positions each frame.
//
// Field names here match what queryAllSatellites() / SatelliteRTree.js
// actually produce: `satelliteId` (not norad_id/id), and `line1`/`line2`
// (not tle_line1/tle_line2 — those are the raw export field names, but
// the R-tree box normalizes them to line1/line2 before this ever sees them).
// =========================================================
function useLivePropagation(satellitesRaw, { batchSize = 150, updateIntervalMs = 1500} = {}) {
  const satrecCache = useRef(new Map());
  const stateRef = useRef(new Map());
  const [, forceTick] = useState(0);

  const keyed = useMemo(() => {
    return satellitesRaw
      .map((sat) => {
        const key = sat.satelliteId ?? sat.satellite_id;
        const tle1 = sat.line1;
        const tle2 = sat.line2;
        if (!key || !tle1 || !tle2) return null;
        return { key, sat, tle1, tle2 };
      })
      .filter(Boolean);
  }, [satellitesRaw]);

  useEffect(() => {
    const cache = satrecCache.current;
    const seen = new Set();
    const now = new Date();

    for (const { key, tle1, tle2 } of keyed) {
      seen.add(key);
      if (!cache.has(key)) {
        try {
          const satrec = satellite.twoline2satrec(tle1, tle2);
          cache.set(key, satrec);
          try {
            const posVel = satellite.propagate(satrec, now);
            if (posVel?.position && typeof posVel.position === 'object') {
              const { x, y, z } = posVel.position;
              if ([x, y, z].every((v) => typeof v === 'number' && isFinite(v))) {
                const initialPos = new THREE.Vector3(x * SCALE, y * SCALE, z * SCALE);
                stateRef.current.set(key, {
                  prev: initialPos.clone(),
                  target: initialPos,
                  lastUpdate: Date.now(),
                });
              }
            }
          } catch {
            // leave unseeded; batched loop will retry later
          }
        } catch {
          // bad TLE, skip entirely
        }
      }
    }

    for (const key of cache.keys()) {
      if (!seen.has(key)) {
        cache.delete(key);
        stateRef.current.delete(key);
      }
    }

  }, [keyed]);
 
  const batchCursor = useRef(0);
  const accumulator = useRef(0);
 
  useFrame((_, delta) => {
    const total = keyed.length;
    if (total === 0) return;
 
    accumulator.current += delta * 1000;
    const tickInterval = updateIntervalMs / Math.max(1, total / batchSize);
    if (accumulator.current < tickInterval) return;
    accumulator.current = 0;
 
    const now = Date.now();
    const nowDate = new Date(now);
 
    for (let i = 0; i < Math.min(batchSize, total); i++) {
      const idx = (batchCursor.current + i) % total;
      const { key } = keyed[idx];
      const satrec = satrecCache.current.get(key);
      if (!satrec) continue;
 
      let posVel;
      try {
        posVel = satellite.propagate(satrec, nowDate);
      } catch {
        continue; // bad propagation this tick — skip, keep last known position
      }
 
      if (!posVel || !posVel.position || typeof posVel.position !== 'object') continue;
      const { x, y, z } = posVel.position;
      if (![x, y, z].every((v) => typeof v === 'number' && isFinite(v))) continue;
 
      const nextPos = new THREE.Vector3(x * SCALE, y * SCALE, z * SCALE);
      const entry = stateRef.current.get(key);
 
      if (!entry) {
        stateRef.current.set(key, { prev: nextPos.clone(), target: nextPos, lastUpdate: now });
      } else {
        entry.prev = entry.current ? entry.current.clone() : entry.target.clone();
        entry.target = nextPos;
        entry.lastUpdate = now;
      }
    }
 
    batchCursor.current = (batchCursor.current + batchSize) % total;
    forceTick((t) => (t + 1) % 1000000);
  });
 
  const getInterpolatedPosition = useCallback((key) => {
    const entry = stateRef.current.get(key);
    if (!entry) return null;
 
    const elapsed = Date.now() - entry.lastUpdate;
    const t = Math.min(elapsed / updateIntervalMs, 1);
    const pos = entry.prev.clone().lerp(entry.target, t);
    entry.current = pos;
    return pos;
  }, [updateIntervalMs]);
 
  return { getInterpolatedPosition, keyed };
}
 
export const SatelliteMarkers = ({
  satellites = [],
  issModel,
  hubbleModel,
  jwstModel,
  genericModel,
  onClusterClick,
  selectedCluster,
  onReady,
  onNotReady,
  isConjunctionModeActive = false,
  baseScale, 
  selectedScaleMultiplier = 1.65,
  enableSelectionColor = false,
  showAllOrbits = false,
}) => {
  const earthGroupRef = useRef();
  const dummy = useMemo(() => new THREE.Object3D(), []);
 
  const meshRefs = useRef(new Map());
  const geometryCache = useRef(new Map());
  const materialCache = useRef(new Map());
 
  // =========================================================
  // LIVE PROPAGATION (batched + interpolated, cheap per frame)
  // =========================================================
  const { getInterpolatedPosition } = useLivePropagation(satellites, {
    batchSize: 150,
    updateIntervalMs: 1500,
  });
 
  // =========================================================
  // 1. NORMALIZE POSITIONS (uses live interpolated position when available,
  //    falls back to static x/y/z from props otherwise)
  // =========================================================
  const satellitesWithPos = useMemo(() => {
    const valid = [];
 
    for (const sat of satellites) {
      const key = sat.satelliteId ?? sat.satellite_id;
      const live = key ? getInterpolatedPosition(key) : null;
 
      let pos;
      if (live) {
        pos = live;
      } else {
        const x = sat.x_km ?? sat.x;
        const y = sat.y_km ?? sat.y;
        const z = sat.z_km ?? sat.z;
        if (![x, y, z].every(v => typeof v === "number" && isFinite(v))) continue;
        pos = new THREE.Vector3(x * SCALE, y * SCALE, z * SCALE);
      }
 
      valid.push({
        ...sat,
        category: (sat.category || 'unknown').toLowerCase(),
        position: pos,
      });
    }
 
    return valid;
    // NOTE: intentionally NOT depending on a per-frame changing value here —
    // see the useFrame below that drives re-clustering on a throttle instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [satellites]);
 
  // =========================================================
  // Re-derive clustering on a throttle, not every frame.
  // We bump `clusterTick` periodically to force recompute of positions,
  // without re-running full grid-clustering at 60fps.
  // =========================================================
  const [clusterTick, setClusterTick] = useState(0);
  const clusterAccum = useRef(0);
  useFrame((_, delta) => {
    clusterAccum.current += delta;
    if (clusterAccum.current >= 0.5) { // re-cluster twice a second — smooth enough, cheap enough
      clusterAccum.current = 0;
      setClusterTick((t) => t + 1);
    }
  });
 
  const hasFiredReady = useRef(false);
  const prevSatelliteIdsRef = useRef('');
  const readySinceTickRef = useRef(-1); // the clusterTick value we must exceed before firing ready again

  useEffect(() => {
    const currentIds = satellites.map((s) => s.satelliteId ?? s.satellite_id).sort().join(',');

    if (currentIds !== prevSatelliteIdsRef.current) {
      hasFiredReady.current = false;
      readySinceTickRef.current = clusterTick; // must see a tick AFTER this one
      onNotReady?.();
      prevSatelliteIdsRef.current = currentIds;
    }
  }, [satellites, onNotReady, clusterTick]);

  useEffect(() => {
    if (!hasFiredReady.current && clusterTick > readySinceTickRef.current) {
      hasFiredReady.current = true;
      onReady?.();
    }
  }, [clusterTick, onReady]);

  const livePositions = useMemo(() => {
    // re-pull interpolated positions each throttled tick
    return satellitesWithPos.map((sat) => {
      const key = sat.satelliteId ?? sat.satellite_id;
      const live = key ? getInterpolatedPosition(key) : null;
      return live ? { ...sat, position: live } : sat;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterTick]);
 
  // =========================================================
  // 2. SPLIT OUT SPECIAL SATELLITES (ISS / JWST / HST)
  // =========================================================
  const ISS_MODULE_NAMES = ['harmony', 'zvezda', 'columbus', 'destiny', 'unity', 'zarya', 'nauka'];
  const JWST_NAMES = ['jwst', 'james webb'];
  const HST_NAMES = ['hst'];
 
  const issModules = useMemo(
    () => livePositions.filter((sat) => ISS_MODULE_NAMES.some((n) => sat.name?.toLowerCase().includes(n))),
    [livePositions]
  );
  const jwstSatellites = useMemo(
    () => livePositions.filter((sat) => JWST_NAMES.some((n) => sat.name?.toLowerCase().includes(n))),
    [livePositions]
  );
  const hstSatellites = useMemo(
    () => livePositions.filter((sat) => HST_NAMES.some((n) => sat.name?.toLowerCase().includes(n))),
    [livePositions]
  );
  const otherSatellites = useMemo(
    () => livePositions.filter(
      (sat) => !issModules.includes(sat) && !jwstSatellites.includes(sat) && !hstSatellites.includes(sat)
    ),
    [livePositions, issModules, jwstSatellites, hstSatellites]
  );
 
  // =========================================================
  // 3. SPECIAL CLUSTERS
  // =========================================================
  const computeCluster = (sats, type) => {
    if (!sats.length) return null;
    const center = new THREE.Vector3();
    sats.forEach((s) => center.add(s.position));
    center.divideScalar(sats.length);
    return { id: type, type, x: center.x, y: center.y, z: center.z, members: sats };
  };
 
  const issCluster = useMemo(() => computeCluster(issModules, 'iss'), [issModules]);
  const jwstCluster = useMemo(() => computeCluster(jwstSatellites, 'jwst'), [jwstSatellites]);
  const hstCluster = useMemo(() => computeCluster(hstSatellites, 'hst'), [hstSatellites]);
 
  // =========================================================
  // 4. GRID CLUSTERING (throttled via livePositions above)
  // =========================================================
  const GRID = 0.15;
 
  const satellitesByCategory = useMemo(() => {
    const byCategory = new Map();
    for (const sat of otherSatellites) {
      if (!byCategory.has(sat.category)) byCategory.set(sat.category, []);
      byCategory.get(sat.category).push(sat);
    }
    return byCategory;
  }, [otherSatellites]);
  
  // const clustersByCategory = useMemo(() => {
  //   const byCategory = new Map();
  //   for (const c of satelliteClusters) {
  //     if (!byCategory.has(c.category)) byCategory.set(c.category, []);
  //     byCategory.get(c.category).push(c);
  //   }
  //   return byCategory;
  // }, [satelliteClusters]);
 
  // =========================================================
  // 5. SPECIAL MODEL HIGHLIGHT MATERIAL
  // =========================================================
  const highlightMaterial = useMemo(
    () => new THREE.MeshStandardMaterial({
      color: new THREE.Color('#fbff00'),
      emissive: new THREE.Color('#fbff00'),
      emissiveIntensity: 0.5,
      depthWrite: true,
      depthTest: true,
    }),
    []
  );
 
  const getModelWithHighlight = useCallback((baseScene, shouldHighlight) => {
    if (!baseScene) return null;
    const clone = baseScene.clone(true);
    clone.traverse((child) => {
      if (child.isMesh) {
        child.material = shouldHighlight ? highlightMaterial : child.material.clone();
        child.raycast = THREE.Mesh.prototype.raycast;
        child.frustumCulled = false;
        child.material.depthWrite = true;
        child.material.depthTest = true;
      }
    });
    return clone;
  }, [highlightMaterial]);
 
  const processedISSModel = useMemo(
    () => getModelWithHighlight(issModel?.scene, selectedCluster?.type === 'iss'),
    [issModel, selectedCluster, getModelWithHighlight]
  );
  const processedJWSTModel = useMemo(
    () => getModelWithHighlight(jwstModel?.scene, selectedCluster?.type === 'jwst'),
    [jwstModel, selectedCluster, getModelWithHighlight]
  );
  const processedHSTModel = useMemo(
    () => getModelWithHighlight(hubbleModel?.scene, selectedCluster?.type === 'hst'),
    [hubbleModel, selectedCluster, getModelWithHighlight]
  );
 
  // =========================================================
  // 6. SCALES
  // =========================================================
  const issScale = 0.0035;
  const hubbleScale = 0.00025;
  const jwstScale = 0.00025;
  // const BASE_SCALE = 0.055;
  const DEFAULT_BASE_SCALE = isConjunctionModeActive ? 0.13 : 0.065;
  const BASE_SCALE = baseScale ?? DEFAULT_BASE_SCALE; 
  const SELECTED_SCALE = BASE_SCALE * selectedScaleMultiplier;

 
  // =========================================================
  // 7. RENDER SPECIAL CLUSTER
  // =========================================================
  const renderSpecialCluster = (cluster, model, scale, id) => {
    if (!model || !cluster) return null;
 
    const isSelected = selectedCluster?.type === cluster.type;
    const finalScale = isSelected ? scale * 1.65 : scale;
    const shouldLookAtCenter = cluster.type === 'iss' || cluster.type === 'hst';
 
    let position = new THREE.Vector3(cluster.x, cluster.y, cluster.z);
    const earthRadius = 1;
    const virtualRadius = 0.135;
    const minDistance = earthRadius + virtualRadius;
    const distance = position.length();
    if (distance < minDistance) {
      position = position.clone().normalize().multiplyScalar(minDistance + 0.002);
    }
 
    let lookAtRotation = [-Math.PI / 2, 0, 0];
    if (shouldLookAtCenter) {
      const target = new THREE.Vector3(0, 0, 0);
      const lookAtQuat = new THREE.Quaternion().setFromRotationMatrix(
        new THREE.Matrix4().lookAt(position, target, new THREE.Vector3(0, 1, 0))
      );
      const blenderCorrection = new THREE.Quaternion().setFromEuler(
        cluster.type === 'iss' ? new THREE.Euler(-Math.PI / 2, 0, 0) : new THREE.Euler(Math.PI / 2, 0, 0)
      );
      lookAtRotation = new THREE.Euler().setFromQuaternion(lookAtQuat.multiply(blenderCorrection)).toArray();
    }
 
    return (
      <group
        key={id}
        position={[position.x, position.y, position.z]}
        scale={[finalScale, finalScale, finalScale]}
        rotation={lookAtRotation}
        onPointerDown={(e) => { e.stopPropagation(); onClusterClick?.(cluster); }}
        onPointerOver={() => (document.body.style.cursor = 'pointer')}
        onPointerOut={() => (document.body.style.cursor = 'default')}
        frustumCulled={false}
      >
        <mesh onPointerDown={(e) => { e.stopPropagation(); onClusterClick?.(cluster); }}>
          <sphereGeometry args={[0.2, 16, 16]} />
          <meshBasicMaterial transparent opacity={0} />
        </mesh>
        <primitive
          object={model}
          dispose={null}
          onUpdate={(self) => {
            self.traverse((child) => {
              if (child.isMesh) {
                child.material.depthTest = true;
                child.material.depthWrite = true;
                child.renderOrder = -1;
              }
            });
          }}
        />
      </group>
    );
  };
 
  // =========================================================
  // 8. SMOOTH PER-FRAME POSITION UPDATES (cheap — no reclustering)
  // Runs every frame, re-averages each cluster's current interpolated
  // member positions and writes fresh matrices. Grouping (which
  // satellites belong to which cluster) still only changes on the
  // slower clusterTick throttle above.
  // =========================================================

  const SELECTED_COLOR = useMemo(() => new THREE.Color('#fbff00'), []);

  useFrame(() => {
    for (const [category, sats] of satellitesByCategory.entries()) {
      const mesh = meshRefs.current.get(category);
      if (!mesh) continue;
    
    
      sats.forEach((sat, i) => {
        const key = sat.satelliteId ?? sat.satellite_id;
        const live = key ? getInterpolatedPosition(key) : null;
        const p = live || sat.position;
        if (!p) return;

        const isSelected = selectedCluster?.members?.includes(sat);
        const scale = isSelected ? SELECTED_SCALE : BASE_SCALE;

        dummy.position.copy(p);
        dummy.scale.set(scale, scale, scale);
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);

        if (enableSelectionColor && mesh.instanceColor) {
          const color = isSelected
            ? SELECTED_COLOR
            : new THREE.Color(CATEGORY_COLOR_MAP[category] || CATEGORY_COLOR_MAP['unknown']);
          mesh.setColorAt(i, color);
        }
        
      });

      for (let i = sats.length; i < mesh.count; i++) {
        dummy.position.set(0, 0, 0);
        dummy.scale.set(0, 0, 0);
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);
      }

      mesh.count = sats.length;
      mesh.instanceMatrix.needsUpdate = true;
      if (enableSelectionColor && mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
  });
 
  // =========================================================
  // 9. ORBIT PATH FOR SELECTED SATELLITE (uses raw TLE lines: line1/line2)
  // =========================================================
  const orbitPathPoints = useMemo(() => {
    if (!selectedCluster?.members?.length) return [];
    const rep = selectedCluster.members[0];
    return computeOrbitPath(rep.line1, rep.line2);
  }, [selectedCluster]);
 
  const orbitLineGeometry = useMemo(() => {
    if (!orbitPathPoints.length) return null;
    return new THREE.BufferGeometry().setFromPoints(orbitPathPoints);
  }, [orbitPathPoints]);
 
  const allOrbitGeometries = useMemo(() => {
    if (!showAllOrbits) return [];
    return livePositions
      .map((sat) => {
        const points = computeOrbitPath(sat.line1, sat.line2);
        if (!points.length) return null;
        return {
          key: sat.satelliteId ?? sat.satellite_id,
          geometry: new THREE.BufferGeometry().setFromPoints(points),
        };
      })
      .filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAllOrbits, satellites]); // keyed on the raw satellites prop, not livePositions —
                                    // orbit SHAPE only depends on TLE data (static per pass),
                                    // not on live interpolated position

  // Dispose old geometries when the set changes, to avoid leaking GPU
  // buffers every time this recomputes.
  const prevOrbitGeometriesRef = useRef([]);
  useEffect(() => {
    return () => {
      prevOrbitGeometriesRef.current.forEach((g) => g.geometry.dispose());
    };
  }, []);
  useEffect(() => {
    prevOrbitGeometriesRef.current.forEach((g) => g.geometry.dispose());
    prevOrbitGeometriesRef.current = allOrbitGeometries;
  }, [allOrbitGeometries]);


  const MAX_INSTANCES_PER_CATEGORY = 6000;
  // =========================================================
  // 10. RENDER
  // =========================================================
  return (
    <group ref={earthGroupRef}>
      {renderSpecialCluster(issCluster, processedISSModel, issScale, 'iss')}
      {renderSpecialCluster(jwstCluster, processedJWSTModel, jwstScale, 'jwst')}
      {renderSpecialCluster(hstCluster, processedHSTModel, hubbleScale, 'hst')}
 
      {orbitLineGeometry && (
        <line geometry={orbitLineGeometry}>
          <lineBasicMaterial color="#00ffff" linewidth={1} />
        </line>
      )}

      {showAllOrbits && allOrbitGeometries.map(({ key, geometry }) => (
        <line key={key} geometry={geometry}>
          <lineBasicMaterial color="#00ffff" linewidth={1}/>
        </line>
      ))}

      {Array.from(satellitesByCategory.entries()).map(([category, sats]) => {
        const geometry = geometryCache.current.get(category) || new THREE.SphereGeometry(0.05, 16, 16);
        geometryCache.current.set(category, geometry);

        const material = materialCache.current.get(category) || new THREE.MeshBasicMaterial({
          color: new THREE.Color(CATEGORY_COLOR_MAP[category] || CATEGORY_COLOR_MAP['unknown']),
        });
        materialCache.current.set(category, material);

        return (
        <instancedMesh
          key={category}
          ref={(ref) => { if (ref) meshRefs.current.set(category, ref); }}
          args={[geometry, material, MAX_INSTANCES_PER_CATEGORY]}
          frustumCulled={false}
          onPointerDown={(e) => {
            e.stopPropagation();
            const sat = sats[e.instanceId];
            if (sat) onClusterClick?.({ id: sat.satelliteId, category, members: [sat] });
          }}
          onPointerOver={() => (document.body.style.cursor = 'pointer')}
          onPointerOut={() => (document.body.style.cursor = 'default')}
        />
      );
    })}
    </group>
  );
};


export const SatelliteView = forwardRef (({
  satellites = [],
  onClusterCount,
  setSatelliteName,
  setSatelliteCountry,
  setLaunchDate,
  setSatelliteLat,
  setSatelliteLon,
  setSatelliteAltitude,
  setSatelliteVelocity,
  setDisposeAssets,
  setIsIssSelected,
  setIsHstSelected,
  setClonedModel,
  setConjunctions,
  isConjunctionModeActive,
}, ref) => {
  const controlsRef = useRef();

  useImperativeHandle(ref, () => ({
    resetCamera: () => {
      controlsRef.current?.reset();
    },
  }));
  const [issModel, setISSModel] = useState(null);
  const [hubbleModel, setHubbleModel] = useState(null);
  const [jwstModel, setJWSTModel] = useState(null);
  const [genericModel, setGenericModel] = useState(null);
  const [modelsReady, setModelsReady] = useState(false);   // renamed from `ready`
  const [positionsReady, setPositionsReady] = useState(false); // NEW
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [isIssSelected, setIsIssSelectedLocal] = useState(false);
  const [isHstSelected, setIsHstSelectedLocal] = useState(false);
  const [localClonedModel, setLocalClonedModel] = useState(null);

  const ISS_MODULE_NAMES = ['harmony', 'zvezda', 'columbus', 'destiny', 'unity', 'zarya', 'nauka'];
  const JWST_NAMES = ['jwst', 'james webb'];
  const HST_NAMES = ['hst'];

  // Overall gate for the loading overlay — both models AND real
  // satellite positions need to be ready before we clear it.
  const fullyReady = modelsReady && positionsReady;

  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0;

  function disposeMaterial(material) {
    for (const key in material) {
      const value = material[key];
      if (value && typeof value.dispose === 'function') {
        value.dispose();
      }
    }
    material.dispose();
  }

  function disposeObject(obj) {
    obj.traverse((child) => {
      if (child.geometry) {
        child.geometry.dispose();
      }

      if (child.material) {
        if (Array.isArray(child.material)) {
          child.material.forEach(disposeMaterial);
        } else {
          disposeMaterial(child.material);
        }
      }
    });

    if (obj.parent) {
      obj.parent.remove(obj);
    }
  }

  useEffect(() => {
    if (!setDisposeAssets || !hubbleModel?.scene) return;
    setDisposeAssets(() => async (options = {}) => {
      const { fromBackButton = false } = options;

      if (fromBackButton) {
        if (isIssSelected) {
          for (let i = 1; i <= 9; i++) {
            clearAsset(`issp${i}`);
          }
        }
        if (isHstSelected && localClonedModel) {
          disposeObject(localClonedModel);
          setLocalClonedModel(null);
          setClonedModel(null);
        }
      } else {
        if (!isIssSelected && !isHstSelected) {
          clearAsset('iss');
          clearAsset('hubble');
          clearAsset('jwst');
          clearAsset('satellite');

          setISSModel(null);
          setHubbleModel(null);
          setJWSTModel(null);
          setGenericModel(null);
        } else if (isIssSelected) {
          clearAsset('iss');
          clearAsset('hubble');
          clearAsset('jwst');
          clearAsset('satellite');

          setISSModel(null);
          setHubbleModel(null);
          setJWSTModel(null);
          setGenericModel(null);
        } else if (isHstSelected) {
          clearAsset('iss');
          clearAsset('jwst');
          clearAsset('satellite');

          setISSModel(null);
          setJWSTModel(null);
          setGenericModel(null);

          const cloned = hubbleModel.scene.clone(true);
          setLocalClonedModel(cloned);
          setClonedModel(cloned);
        }
      }
    });
  }, [setDisposeAssets, hubbleModel, isHstSelected, isIssSelected, setClonedModel, localClonedModel]);

  useEffect(() => {
    if (!selectedCluster) return;

    let stillPresent;

    if (selectedCluster.type === 'iss') {
      stillPresent = satellites.some((sat) =>
        ISS_MODULE_NAMES.some((n) => sat.name?.toLowerCase().includes(n))
      );
    } else if (selectedCluster.type === 'jwst') {
      stillPresent = satellites.some((sat) =>
        JWST_NAMES.some((n) => sat.name?.toLowerCase().includes(n))
      );
    } else if (selectedCluster.type === 'hst') {
      stillPresent = satellites.some((sat) =>
        HST_NAMES.some((n) => sat.name?.toLowerCase().includes(n))
      );
    } else {
      const selectedSat = selectedCluster.members?.[0];
      if (!selectedSat) return;
      stillPresent = satellites.some(
        (sat) => sat.satelliteId === selectedSat.satelliteId
      );
    }

    if (!stillPresent) {
      setSatelliteName?.('');
      setSatelliteCountry?.('');
      setLaunchDate?.('');
      setSatelliteLat?.('');
      setSatelliteLon?.('');
      setSatelliteAltitude?.('');
      setSatelliteVelocity?.('');
      setIsIssSelected?.(false);
      setIsHstSelected?.(false);
      setIsIssSelectedLocal(false);
      setIsHstSelectedLocal(false);
      setConjunctions?.([]);
      setSelectedCluster(null);
    }
  }, [satellites, selectedCluster, setSatelliteName, setSatelliteCountry, setLaunchDate,
      setSatelliteLat, setSatelliteLon, setSatelliteAltitude, setSatelliteVelocity,
      setIsIssSelected, setIsHstSelected, setConjunctions]);


  const handleClusterClick = (cluster) => {
    console.log('Clicked cluster:', cluster.members);
    const { members } = cluster;
    if (members?.length > 0) {
      const sat = members[0];
      if (sat?.name && !sat.name.toLowerCase().includes('iss (')) {
        setSatelliteName?.(`${sat.satelliteId} - ${sat.name}`);
        setSatelliteCountry?.(sat.country);
        setLaunchDate?.(sat.launch);
        setSatelliteLat?.(`Latitude: ${sat.lat}`);
        setSatelliteLon?.(`Longtitude: ${sat.lon}`);
        setSatelliteAltitude?.(`Altitude: ${sat.altitude} km`);
        setSatelliteVelocity?.(`Velocity: ${sat.speed} km/h`);
        setIsIssSelected?.(false);
        setIsHstSelected?.(false);
        setIsIssSelectedLocal(false);
        setIsHstSelectedLocal(false);
        setConjunctions?.(getConjunctionsForSatellite(sat.satelliteId));

        if (sat.name.toLowerCase().includes('hst')) {
          setSatelliteName?.(`${sat.satelliteId} - ${sat.name} (Hubble Space Telescope)`);
          setSatelliteLat?.(`Latitude: ${sat.lat}`);
          setSatelliteLon?.(`Longtitude: ${sat.lon}`);
          setSatelliteAltitude?.(`Altitude: ${sat.altitude} km`);
          setSatelliteVelocity?.(`Velocity: ${sat.speed} km/h`);
          setIsHstSelected?.(true);
          setIsHstSelectedLocal(true);
        }
      } else if (sat?.name && sat.name.toLowerCase().includes('iss (')) {
        setSatelliteName?.(`ISS - International Space Station`);
        setSatelliteCountry?.(`US, CIS, ESA`);
        const firstModule = members.find(m =>
          m.name?.toLowerCase().includes('zarya')
        ) || members.find(m => m.launch);

        setLaunchDate?.(firstModule?.launch || 'Unknown');
        setIsIssSelected?.(true);
        setIsHstSelected?.(false);
        setIsIssSelectedLocal(true);
        setIsHstSelectedLocal(false);
        setConjunctions?.(getConjunctionsForSatellite(sat.satelliteId));
      }
    }
    setSelectedCluster(cluster);
  };

  return (
    <>
      {!fullyReady && (
        <Html fullscreen>
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: 'rgba(15, 15, 15, 0.65)',
              backdropFilter: 'blur(20px)',
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: "white",
              fontSize: "24px",
            }}
          >
            Loading satellites...
          </div>
        </Html>
      )}
      <SatellitePreloader
        onComplete={(loadedAssets) => {
          console.log("Loaded assets:", loadedAssets);
          setISSModel(loadedAssets.iss);
          setHubbleModel(loadedAssets.hubble);
          setJWSTModel(loadedAssets.jwst);
          setGenericModel(loadedAssets.satellite);
          setModelsReady(true);
        }}
      />
      <TrackballControls
        ref={controlsRef}
        rotateSpeed={isMobile ? 4.0 : 6.0}
        zoomSpeed={1.2}
        panSpeed={0}
        noPan
        enableZoom
        enablePan={false}
        staticMoving
        minDistance={2}
        maxDistance={50}
      />
      {modelsReady && (
        <SatelliteMarkers
          satellites={satellites}
          issModel={issModel}
          hubbleModel={hubbleModel}
          jwstModel={jwstModel}
          genericModel={genericModel}
          onClusterCount={onClusterCount}
          onClusterClick={handleClusterClick}
          selectedCluster={selectedCluster}
          onReady={() => setPositionsReady(true)}
          onNotReady={() => setPositionsReady(false)}
          isConjunctionModeActive={isConjunctionModeActive}
        />
      )}
    </>
  );
});
